// External Libraries
import React from "react";
import Modal from "react-responsive-modal";
import * as Icon from "react-feather";

// Services
import CommunicationService from "../../../services/CommunicationService";
import UtilityService from "../../../services/UtilityService";

// Common Components
import WComponent from "../../common/WComponent";
import Button from "../../common/button/Button";
import Loader from "../../common/loader/Loader";

const { alert } = UtilityService;

class MeetSyncModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			importing: false,
			driveDocs: [],
			selectedFileIds: new Set(),
		};
	}

	componentDidUpdate = prevProps => {
		let { show } = this.props;

		if (prevProps.show === false && show === true) {
			this.fetchData();
		}
	};

	fetchData = async () => {
		let { clientId } = this.props;

		await this.update({
			loading: true,
			driveDocs: [],
			selectedFileIds: new Set(),
		});

		try {
			const result = await CommunicationService.listMeetRecordings({ clientId });

			if (result) {
				await this.update({ driveDocs: result.driveDocs || [] });
			}
		} catch (error) {
			console.log(error);
			alert({ type: "error", text: "Could not load meeting data. Check your Google account connection." });
		}

		await this.update({ loading: false });
	};

	onToggleDoc = fileId => {
		let { selectedFileIds } = this.state;
		const next = new Set(selectedFileIds);
		next.has(fileId) ? next.delete(fileId) : next.add(fileId);
		this.update({ selectedFileIds: next });
	};

	onImport = async () => {
		let { clientId, onClose } = this.props;
		let { selectedFileIds } = this.state;

		if (selectedFileIds.size === 0) return;

		await this.update({ importing: true });

		try {
			const result = await CommunicationService.importMeetRecordings({
				clientId,
				fileIds: Array.from(selectedFileIds),
			});

			if (result) {
				const failedCount = (result.failed && result.failed.length) || 0;

				if (result.imported > 0) {
					alert({
						type: "success",
						text:
							`${result.imported} transcript${result.imported !== 1 ? "s" : ""} imported. AI summarisation running in background.` +
							(failedCount > 0 ? ` ${failedCount} file${failedCount !== 1 ? "s" : ""} could not be imported.` : ""),
					});

					if (result.folderLinked === false) {
						alert({ type: "info", text: "This client has no linked Drive folder, so no transcript copy was saved there." });
					}

					onClose();
				} else if (failedCount > 0) {
					alert({
						type: "error",
						text: `None of the ${failedCount} selected file${failedCount !== 1 ? "s" : ""} could be imported. Please try again.`,
					});
				} else {
					alert({ type: "info", text: "Nothing new to import — these transcripts are already logged." });
					onClose();
				}
			}
		} catch (error) {
			console.log(error);
			alert({ type: "error", text: "Import failed. Please try again." });
		}

		await this.update({ importing: false });
	};

	renderDocItem = doc => {
		let { selectedFileIds } = this.state;
		const isSelected = selectedFileIds.has(doc.fileId);

		return (
			<div
				key={doc.fileId}
				className={`meet-sync-modal__item${isSelected ? " meet-sync-modal__item--selected" : ""}`}
				onClick={() => this.onToggleDoc(doc.fileId)}
			>
				<input type="checkbox" readOnly checked={isSelected} />
				<div className="meet-sync-modal__item__info">
					<div className="meet-sync-modal__item__title">{doc.title}</div>
					<div className="meet-sync-modal__item__date">
						{UtilityService.formatDate({ value: doc.createdAt })}
					</div>
					{doc.matchedMeetingTitle && (
						<div className="meet-sync-modal__item__match">
							<Icon.Link size={11} />
							Matched: {doc.matchedMeetingTitle}
						</div>
					)}
				</div>
			</div>
		);
	};

	render() {
		let { show, onClose } = this.props;
		let { loading, importing, driveDocs, selectedFileIds } = this.state;

		const matchedDocs = driveDocs.filter(d => d.matchedMeetingId);
		const unmatchedDocs = driveDocs.filter(d => !d.matchedMeetingId);
		const totalSelected = selectedFileIds.size;

		return (
			<Modal open={show} onClose={onClose} classNames={{ modal: "meet-sync-modal" }} center>
				<div className="meet-sync-modal__header">
					<Icon.Video size={20} />
					<span>Import from Meet Recordings</span>
				</div>

				{loading && (
					<div className="meet-sync-modal__loader">
						<Loader />
						<span>Loading...</span>
					</div>
				)}

				{!loading && driveDocs.length === 0 && (
					<div className="meet-sync-modal__empty">
						<Icon.Inbox size={40} />
						<div>No transcripts found in your Meet Recordings folder.</div>
					</div>
				)}

				{!loading && matchedDocs.length > 0 && (
					<>
						<p className="meet-sync-modal__section-label">Matched to scheduled meetings</p>
						<div className="meet-sync-modal__list">
							{matchedDocs.map(this.renderDocItem)}
						</div>
					</>
				)}

				{!loading && unmatchedDocs.length > 0 && (
					<>
						<p className="meet-sync-modal__section-label meet-sync-modal__section-label--secondary">
							Other recordings
						</p>
						<div className="meet-sync-modal__list">
							{unmatchedDocs.map(this.renderDocItem)}
						</div>
					</>
				)}

				{!loading && driveDocs.length > 0 && (
					<div className="meet-sync-modal__note">
						<Icon.Info size={13} />
						<span>A copy of each imported transcript is saved to this client's Communications folder, if a Drive folder is linked.</span>
					</div>
				)}

				<div className="meet-sync-modal__footer">
					<Button onClick={onClose} view="secondary" text="Cancel" disabled={importing} />
					<Button
						onClick={this.onImport}
						view="primary"
						text={importing ? "Importing..." : `Import${totalSelected > 0 ? ` (${totalSelected})` : ""}`}
						disabled={importing || totalSelected === 0}
					/>
				</div>
			</Modal>
		);
	}
}

export default MeetSyncModal;
